import Cookies from 'cookie-universal'
import { v4 as uuidv4 } from 'uuid'
import type { CartProduct, Product } from '@mc/api'
import { GrowthService } from '@mc/api'
import { AUTH_TYPE } from '@mc/constants'
import { getGDPRCheckboxValue } from '@mc/gdpr'
import {
  getDefaultSubscriptionPlanPropertiesAsync,
  getLTVPriceValueFromProduct,
} from '@mc/products'
import {
  adPartnersTo,
  trackGoldStandardEvent,
  identify,
  PageSource,
  CartEvents,
  PageLoadEvents,
} from '@mc/track-event'
import type {
  CartEmailCaptureEventProps,
  CartEmailCaptureOnHeroEventProps,
  CartEmailCaptureEventDetails,
} from '../../types/CartEmailCaptureEventProps'

export const trackCartEmailCaptured = async ({
  location = 'falcon_cart',
  type = AUTH_TYPE.EMAIL,
  email,
  pageSource = PageLoadEvents.PLANS,
  captureMethod = CartEvents.EMAIL_GATHER.USER_ADDED,
  isGift = false,
  product,
  ...freeTrial
}: CartEmailCaptureEventProps) => {
  const revenue = product ? getLTVPriceValueFromProduct(isGift, product) : null

  const eventDetails: CartEmailCaptureEventDetails = {
    ...baseProps(product),
    location,
    type,
    email,
    page_source: pageSource,
    email_capture_method: captureMethod,
    ...(revenue && { revenue }),
    ...freeTrial,
    event_id: uuidv4(),
    marketing_consent: !!getGDPRCheckboxValue(),
  }

  await sendEmailCaptureTracking({
    email,
    eventDetails,
  })
}

const baseProps = (product?: CartProduct | Product) => {
  const cookies = Cookies()
  const allCmPagesViewed = cookies.get('all_cm_pages_viewed')
  const lastCmPageViewed = cookies.get('last_cm_page')

  const productProps = product
    ? {
        content_type: 'product',
        product_id: `${product.id}`,
      }
    : {}

  return {
    ...productProps,
    currency: product?.currency || 'USD',
    quantity: 1,
    product: 'core', // TODO https://masterclass-dev.atlassian.net/browse/PEN-4307
    gdpr_opt_in: !!getGDPRCheckboxValue(), // Deprecated in favor of marketing_consent
    marketing_consent: !!getGDPRCheckboxValue(),
    ...(allCmPagesViewed && { all_cm_pages_viewed: allCmPagesViewed }),
    ...(lastCmPageViewed && { last_cm_page_viewed: lastCmPageViewed }),
  }
}

export const trackCartEmailCapturedOnHero = async ({
  location = 'hero',
  type = AUTH_TYPE.EMAIL,
  email,
  pageSource = PageSource.HOMEPAGE,
  captureMethod = CartEvents.EMAIL_GATHER.USER_ADDED,
  nextCaptureMethod,
  ...freeTrial
}: CartEmailCaptureOnHeroEventProps) => {
  const product = (await getDefaultSubscriptionPlanPropertiesAsync()) || {}

  const eventDetails: CartEmailCaptureEventDetails = {
    ...baseProps(product),
    location,
    type,
    revenue: getLTVPriceValueFromProduct(false, product),
    ...(email && { email }),
    page_source: pageSource,
    email_capture_method: captureMethod,
    ...freeTrial,
    event_id: uuidv4(),
  }

  await sendEmailCaptureTracking({
    email,
    eventDetails,
  })
}

const sendEmailCaptureTracking = async ({
  email,
  eventDetails,
}: {
  email: string | undefined
  eventDetails: CartEmailCaptureEventDetails
}) => {
  // If cookies are enabled: sends an identify immediately
  // If cookies are disabled: queues an identify that will send if cookies are enabled later
  if (email) {
    identify(null, { email, integrations: { Iterable: true } })
  }

  // Send essential data to backend for Iterable tracking
  // This ensures the event reaches Iterable even when Segment/cookies are blocked
  if (email) {
    const essentialEmailCaptureParams = {
      email,
      event_name: CartEvents.EVENTS.CART_EMAIL_CAPTURED_GOLD_STANDARD,
      marketing_consent: !!getGDPRCheckboxValue(),
      product: 'core', // TODO https://masterclass-dev.atlassian.net/browse/PEN-4307
      event_id: eventDetails.event_id,
    }

    // Track essential data on backend via Iterable
    try {
      await GrowthService.createEmailCapture({
        requestBody: essentialEmailCaptureParams,
      })
    } catch {
      // Fail silently - we still want to try Segment tracking below
    }
  }

  // Track additional data via Segment from frontend with the same event name and ID
  trackGoldStandardEvent(
    CartEvents.EVENTS.CART_EMAIL_CAPTURED_GOLD_STANDARD,
    eventDetails,
    {
      integrations: adPartnersTo(true),
      messageId: eventDetails.event_id, // messageId will deduplicate the event in Iterable
    },
  )
}
